import React, { MouseEvent, KeyboardEvent } from "react";
import styles from "./machines-remote-support-requested-button.module.scss";
import vpnRemoteSupportIconOrange from "@src/assets/svg/machines-remote-support-icon-orange.svg";
import { VerticalSeparator } from "@src/components/atoms/vertical-separator/vertical-separator";
import { useI18N } from "@src/language/language-context";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { CustomTooltip } from "@src/components/atoms/custom-tooltip/custom-tooltip";
import { MachinesListSupportSessionsStatusTypes } from "@common/types/machines-list-types";
import cn from "classnames";
import UpRightOrangeArrowIcon from "@src/assets/svg/up-right-arrow-orange.svg";

const cssClassName = "machines-remote-support-requested-button";

interface MachinesListRemoteSupportRequestedButtonPropsTypes {
  onViewDetails?: (e: MouseEvent | KeyboardEvent) => void;
  onCancel?: (e: MouseEvent | KeyboardEvent) => void;
  buttonText: string;
  loading?: boolean;
  status?: MachinesListSupportSessionsStatusTypes;
  buttonVariant?: string;
  disableAllActions?: boolean;
  isClosingState?: boolean;
  tooltip?: string;
  allowInProgressViewDetails?: boolean;
  showJoinSessionIcon?: boolean;
  showCancelIcon?: boolean;
}

export const MachinesListRemoteSupportRequestedButton = ({
  onViewDetails,
  onCancel,
  buttonText,
  loading,
  status,
  buttonVariant = "request-modal-button",
  disableAllActions = false,
  isClosingState = false,
  tooltip = "",
  allowInProgressViewDetails = false,
  showJoinSessionIcon = false,
  showCancelIcon = true,
}: MachinesListRemoteSupportRequestedButtonPropsTypes) => {
  const i18n = useI18N();

  const handleCancelClick = (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
    if (disableAllActions) return;
    onCancel?.(e);
  };

  const handleViewClick = (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
    onViewDetails?.(e);
  };

  const texts = {
    viewDetails: i18n(
      "modules.machinesRemoteSupportRequestedButton.viewDetails",
    ),
    cancel: i18n("modules.machinesRemoteSupportRequestedButton.cancel"),
    requestedIcon: i18n(
      "modules.machinesRemoteSupportRequestedButton.requestedIcon",
    ),
    joinSessionIcon: i18n(
      "modules.machinesRemoteSupportRequestedButton.joinSessionIcon",
    ),
  };

  const isDisabled =
    disableAllActions ||
    (status === MachinesListSupportSessionsStatusTypes.IN_PROGRESS &&
      !allowInProgressViewDetails);
  const disabledButtonStyle = isDisabled ? { opacity: 0.4 } : undefined;

  let isTicketVariant = false;
  switch (buttonVariant) {
    case "request-modal-ticket-button":
      isTicketVariant = true;
      break;
    default:
      isTicketVariant = false;
      break;
  }

  const shouldShowJoinSessionIcon =
    status === MachinesListSupportSessionsStatusTypes.IN_PROGRESS &&
    showJoinSessionIcon;

  return (
    <div
      className={`${styles[cssClassName]} ${
        isClosingState ? styles[`${cssClassName}--closing`] : ""
      }`}
    >
      <CustomTooltip
        content={tooltip}
        maxWidth="360px"
        className={styles[`${cssClassName}__tooltip-wrapper`]}
      >
        <ButtonWithLoader
          fun={handleViewClick}
          text={texts.viewDetails}
          variant={buttonVariant}
          loading={loading}
          disabled={isDisabled}
          style={disabledButtonStyle}
          spinnerVariant={["warning", "small"]}
        >
          <img
            className={styles[`${cssClassName}__icon`]}
            src={vpnRemoteSupportIconOrange}
            alt={texts.requestedIcon}
          />
          <span
            className={cn(styles[`${cssClassName}__text`], {
              [styles[`${cssClassName}__text--extra-small`]]: isTicketVariant,
            })}
          >
            {buttonText}
          </span>
          {shouldShowJoinSessionIcon && (
            <img
              className={styles[`${cssClassName}__join-session-icon`]}
              src={UpRightOrangeArrowIcon}
              alt={texts.joinSessionIcon}
            />
          )}
        </ButtonWithLoader>
      </CustomTooltip>

      {!isClosingState &&
        status !== MachinesListSupportSessionsStatusTypes.IN_PROGRESS && (
          <span className={styles[`${cssClassName}__arrow`]}>›</span>
        )}

      {!isClosingState && showCancelIcon && (
        <>
          <VerticalSeparator />
          <CustomTooltip
            content={disableAllActions ? tooltip : ""}
            maxWidth="360px"
          >
            <button
              type="button"
              className={styles[`${cssClassName}__cancel-button`]}
              onClick={handleCancelClick}
              aria-label={texts.cancel}
              disabled={disableAllActions}
            >
              <span className={styles[`${cssClassName}__cancel-icon`]}>×</span>
            </button>
          </CustomTooltip>
        </>
      )}
    </div>
  );
};
